/**
 * What the drawer's Email row says about a run's delivery — spec 137 decision 10.
 *
 * ## Read the STATUS, never `deliveredAt`
 *
 * `deliveredAt` is `notified_at`, the send-once guard. It means "delivery finished,
 * whatever the outcome", and five server paths set it — an accepted send, a refusal
 * given up on after ten tries, an owner with no email claim, an expired sign-in, a run
 * that never finished. The old precedence (`deliveredAt ? 'Sent to you' : …`) therefore
 * labelled the LIVE give-up row — D1 job fbe36df5, `notified_at` + "One or more mail
 * server settings have not been saved…", 10 attempts — "Sent to you". It never rendered
 * only because no job API sent the fields at all (spec 132 decision 3).
 *
 * ## Never "delivered", never "sent to you"
 *
 * CB answers `{success:true}` even when its own send failed (`ServerFiles.cs:467-495`,
 * `EmailController.cs:367`), so the most any status can claim is that Centerbase
 * ACCEPTED the email for delivery. Receipt is only in CB's MailServerLog, which no API
 * reads.
 *
 * ## Legacy rows
 *
 * A row finished before migration 0015 has no status. With `deliveryError` it is the
 * give-up shape; without, it is CB's accept — though an expired sign-in and a missing
 * email claim wrote the same bytes, which is exactly why the status column exists.
 *
 * Returns `null` when there is nothing to say (not the owner's run, or no email asked).
 * Otherwise `{ value, note, tone }` for one `Fact` row.
 *
 * `tone: 'danger'` (*review* — this line used to claim a rule the code never followed)
 * is reserved for the outcomes where Centerbase was ASKED and refused, or answered
 * without confirming: `failed`, `unconfirmed`, and a refusal still being retried. A
 * send that was never attempted by decision — `skipped`, a run that stopped, an
 * expired result — is stated plainly in the body grey: nothing went wrong in the
 * mailer, and the sentence itself says the email was not sent.
 *
 * ## Only what the row can back (*review*)
 *
 * "Sending…" is present progressive, so it is said only where a send is actually
 * due: a READY run whose delivery is pending. A run still queued, running or booked
 * for later reads "Not sent yet" — nothing is being sent, and saying so was the claim
 * the old pending branch made for a run due next Tuesday. And a status this bundle
 * does not know (a newer worker) is never read as the one positive claim the drawer
 * makes: it says the outcome is not recognised.
 */
const STATUS = Object.freeze({
  ACCEPTED: 'accepted',
  UNCONFIRMED: 'unconfirmed',
  FAILED: 'failed',
  SKIPPED: 'skipped',
})

export const ACCEPTED_LINE = 'Accepted by Centerbase for delivery to you'
export const UNCONFIRMED_LINE = 'Centerbase didn’t confirm it was sent'
export const NOT_SENT_LINE = 'Not sent'
export const NOT_SENT_YET_LINE = 'Not sent yet'
export const SENDING_LINE = 'Sending…'
export const UNRECOGNISED_LINE = 'Outcome not recognised'
// The server writes the same sentence onto the row (cb-api delivery.js
// DELIVERY_NOTES.runDidntFinish); the client says it before the next tick does.
// Byte-identical to the server's, straight apostrophe included, so the drawer never
// shows two spellings of one sentence (and one locator matches both).
export const RUN_DIDNT_FINISH_NOTE = "The run didn't finish, so nothing was emailed."

/** "Couldn’t be sent", plus "after N tries" when there was more than one. */
function couldntBeSent(attempts) {
  return attempts > 1 ? `Couldn’t be sent after ${attempts} tries` : 'Couldn’t be sent'
}

export function deliveryOutcome(job, state = job?.effectiveState || job?.state) {
  if (!job || job.isOwner === false || !job.deliverEmail) return null
  const attempts = Number(job.deliveryAttempts) || 0
  const error = job.deliveryError || null
  const note = job.deliveryNote || null

  switch (job.deliveryStatus) {
    case STATUS.ACCEPTED:
      // The note, when present, is the downgrade ("…so the email carries a link instead").
      return { value: ACCEPTED_LINE, note, tone: undefined }
    case STATUS.UNCONFIRMED:
      return { value: UNCONFIRMED_LINE, note, tone: 'danger' }
    case STATUS.FAILED:
      // CB's OWN words for the refusal, not paraphrased and not replaced with advice:
      // a guess about the cause is how a reader ends up changing the wrong thing.
      return { value: couldntBeSent(attempts), note: error || note, tone: 'danger' }
    case STATUS.SKIPPED:
      return { value: NOT_SENT_LINE, note, tone: undefined }
    case null:
    case undefined:
      break
    default:
      // *review* — a status this bundle does not know. Neutral, never the accept line.
      return { value: UNRECOGNISED_LINE, note: note || error, tone: undefined }
  }

  // No status: a legacy row, or still pending.
  if (job.deliveredAt != null) {
    return error
      ? { value: couldntBeSent(attempts), note: error, tone: 'danger' }
      : { value: ACCEPTED_LINE, note: null, tone: undefined }
  }
  // Pending. A run that stopped can never be emailed — the server records exactly
  // this on its next pass (`terminalJobFields`); saying "Sending…" until then would
  // be a claim the row cannot back.
  if (state === 'failed' || state === 'cancelled') {
    return { value: NOT_SENT_LINE, note: RUN_DIDNT_FINISH_NOTE, tone: undefined }
  }
  // The delivery pass only takes unexpired results (`listDeliverableJobs`), so a
  // pending send on an expired result will never happen either.
  if (state === 'expired') {
    return { value: NOT_SENT_LINE, note: 'The result expired before it could be emailed.', tone: undefined }
  }
  // *review* — queued, running, scheduled, recurring: nothing is being sent yet.
  if (state !== 'ready') {
    return { value: NOT_SENT_YET_LINE, note: 'once the run finishes', tone: undefined }
  }
  if (error) {
    // Refused at least once and still being retried (bounded server-side).
    return { value: SENDING_LINE, note: `Last try refused: ${error}`, tone: 'danger' }
  }
  return { value: SENDING_LINE, note: 'usually within a few minutes', tone: undefined }
}

/** Spec 138: a recipient's accept line. Not "…to you" — the reader is the owner, not them. */
export const RECIPIENT_ACCEPTED_LINE = 'Accepted by Centerbase for delivery'
export const RECIPIENTS_UNREADABLE_NOTE = 'This run’s list of other recipients couldn’t be read, so what happened to them is unknown.'

/**
 * Spec 138 §3.7 — the drawer line for ONE of a schedule's other recipients, in spec 137's
 * vocabulary and under the same rules: the status decides, never "delivered", never
 * "sent to", `danger` only where Centerbase was asked and refused or did not confirm.
 * Each recipient has its own status (`pending` until the delivery pass decides it), so
 * a pending one is read against the RUN's state exactly as the owner's own row is.
 *
 * Returns `{ value, note, tone }`, or null when there is nothing to say.
 */
export function recipientOutcome(recipient, job, state = job?.effectiveState || job?.state) {
  if (!recipient || !job || job.isOwner === false || !job.deliverEmail) return null
  const attempts = Number(recipient.attempts) || 0
  const error = recipient.error || null
  const note = recipient.note || null
  switch (recipient.status) {
    case STATUS.ACCEPTED:
      return { value: RECIPIENT_ACCEPTED_LINE, note, tone: undefined }
    case STATUS.UNCONFIRMED:
      return { value: UNCONFIRMED_LINE, note, tone: 'danger' }
    case STATUS.FAILED:
      return { value: couldntBeSent(attempts), note: error || note, tone: 'danger' }
    case STATUS.SKIPPED:
      return { value: NOT_SENT_LINE, note, tone: undefined }
    case 'pending':
    case null:
    case undefined:
      break
    default:
      return { value: UNRECOGNISED_LINE, note: note || error, tone: undefined }
  }
  // Pending — read against the run, as the owner's pending row is.
  if (job.deliveredAt != null) return { value: NOT_SENT_LINE, note, tone: undefined }
  if (state === 'failed' || state === 'cancelled') return { value: NOT_SENT_LINE, note: RUN_DIDNT_FINISH_NOTE, tone: undefined }
  if (state === 'expired') return { value: NOT_SENT_LINE, note: 'The result expired before it could be emailed.', tone: undefined }
  if (state !== 'ready') return { value: NOT_SENT_YET_LINE, note: 'once the run finishes', tone: undefined }
  if (error) return { value: SENDING_LINE, note: `Last try refused: ${error}`, tone: 'danger' }
  return { value: SENDING_LINE, note: 'usually within a few minutes', tone: undefined }
}
